if (!customElements.get('added-notification')) {
  const AddedNotification = class extends HTMLElement {
    connectedCallback() {
      this.dismissOthers();
      this.continueButton = this.querySelector('.cart-summary-overlay__continue');
      if (this.continueButton) this.continueButton.addEventListener('click', this.dismiss.bind(this));
      this.escapeHandler = (evt) => { if (evt.key === 'Escape') this.dismiss(); };
      document.addEventListener('keydown', this.escapeHandler);
      this.scrollHandler = () => this.dismiss();
      window.addEventListener('scroll', this.scrollHandler, { once: true, passive: true });

      this.renderItem(this.itemData || {});
      requestAnimationFrame(() => this.classList.remove('cart-summary-overlay--hidden'));
      this.enrich(this.itemData || {});
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.escapeHandler);
      window.removeEventListener('scroll', this.scrollHandler);
    }

    dismissOthers() {
      document.querySelectorAll('added-notification').forEach((notification) => {
        if (notification !== this && typeof notification.dismiss === 'function') notification.dismiss();
      });
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (number == null || Number.isNaN(Number(number))) return '0';
      const parts = (Number(number) / 100).toFixed(precision).split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }

    formatMoney(cents) {
      const format = (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) || '{{amount}}';
      const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return this.formatWithDelimiters(cents, 2, '.', ',');
      let value;
      switch (match[1]) {
        case 'amount': value = this.formatWithDelimiters(cents, 2, ',', '.'); break;
        case 'amount_no_decimals': value = this.formatWithDelimiters(cents, 0, ',', '.'); break;
        case 'amount_with_comma_separator': value = this.formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = this.formatWithDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = this.formatWithDelimiters(cents, 2, "'", '.'); break;
        default: value = this.formatWithDelimiters(cents, 2, '.', ',');
      }
      return format.replace(match[0], value);
    }

    optionMarkup(item) {
      if (Array.isArray(item.options_with_values) && item.options_with_values.length) {
        return item.options_with_values
          .filter((option) => option && option.value && option.value !== 'Default Title')
          .map((option) => `<div class="cart-summary-overlay__variant"><span class="cart-summary-overlay__variant-option">${this.escapeHtml(option.name)}</span><span class="cart-summary-overlay__variant-value">${this.escapeHtml(option.value)}</span></div>`)
          .join('');
      }
      if (Array.isArray(item.variant_options)) {
        return item.variant_options
          .filter((value) => value && value !== 'Default Title')
          .map((value) => `<div class="cart-summary-overlay__variant"><span class="cart-summary-overlay__variant-value">${this.escapeHtml(value)}</span></div>`)
          .join('');
      }
      return '';
    }

    priceMarkup(finalPrice, comparePrice) {
      let html = '';
      if (comparePrice && comparePrice > finalPrice) {
        html += `<div class="cart-summary-overlay__compare-price">${this.escapeHtml(this.formatMoney(comparePrice))}</div>`;
      }
      html += `<div>${this.escapeHtml(this.formatMoney(finalPrice))}</div>`;
      return html;
    }

    renderItem(item, compareAtPrice = 0) {
      const quantity = Number(item.quantity || 1);
      const finalPrice = Number(item.final_price ?? item.price ?? 0);
      const finalLinePrice = Number(item.final_line_price ?? item.line_price ?? finalPrice * quantity);
      const comparePrice = Math.max(Number(compareAtPrice || 0), Number(item.original_price || 0));

      const title = this.querySelector('.cart-summary-overlay__title');
      if (title) title.textContent = item.product_title || item.title || this.dataset.productTitle || '';

      const img = this.querySelector('.cart-summary-overlay__image');
      const imageUrl = (item.featured_image && item.featured_image.url) || item.image || '';
      if (img) {
        img.src = imageUrl;
        img.alt = item.product_title || item.title || '';
      }

      const variants = this.querySelector('.cart-summary-overlay__variants');
      if (variants) variants.innerHTML = this.optionMarkup(item);

      const unitHtml = this.priceMarkup(finalPrice, comparePrice);
      this.querySelectorAll('.cart-summary-overlay__unit-price, .cart-summary-overlay__mobile-unit-price').forEach((el) => { el.innerHTML = unitHtml; });
      this.querySelectorAll('.cart-summary-overlay__quantity').forEach((el) => { el.textContent = quantity; });

      let lineHtml = '';
      if (comparePrice > finalPrice) lineHtml += `<div class="cart-summary-overlay__compare-price">${this.escapeHtml(this.formatMoney(comparePrice * quantity))}</div>`;
      lineHtml += `<div>${this.escapeHtml(this.formatMoney(finalLinePrice))}</div>`;
      this.querySelectorAll('.cart-summary-overlay__line-price, .cart-summary-overlay__mobile-line-price').forEach((el) => { el.innerHTML = lineHtml; });
    }

    async enrich(item) {
      const cartUrl = `${theme.routes.cart}.js`;
      const productUrl = item.url ? `${item.url.split('?')[0]}.js` : null;
      const requests = [fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.ok ? r.json() : null).catch(() => null)];
      requests.push(productUrl ? fetch(productUrl, { headers: { Accept: 'application/json' } }).then((r) => r.ok ? r.json() : null).catch(() => null) : Promise.resolve(null));
      const [cartData, productData] = await Promise.all(requests);

      if (!this.isConnected) return;
      if (cartData && typeof cartData.total_price === 'number') {
        const subtotal = this.querySelector('.cart-summary-overlay__subtotal-amount');
        if (subtotal) subtotal.textContent = this.formatMoney(cartData.total_price);
      }

      if (productData && Array.isArray(productData.variants)) {
        const variantId = Number(item.variant_id || item.id);
        const variant = productData.variants.find((v) => Number(v.id) === variantId);
        if (variant && Number(variant.compare_at_price || 0) > 0) this.renderItem(item, Number(variant.compare_at_price));
      }
    }

    dismiss(evt) {
      if (evt) evt.preventDefault();
      if (this.classList.contains('cart-summary-overlay--dismissed')) return;
      this.classList.add('cart-summary-overlay--dismissed');
      setTimeout(() => this.remove(), 450);
    }
  };

  window.customElements.define('added-notification', AddedNotification);
}
